/**
 * "Slikaj" — taking the photo inside the page.
 *
 * The phone's own camera, opened from a file picker, runs as a separate app;
 * on an Android phone short of memory the browser tab behind it is closed
 * while the shopper frames their selfie, and the picture never arrives (see
 * photo-intake.ts). This camera never leaves the page: the video is shown
 * right here and one frame becomes the photo. Where the browser has no camera
 * access (an old WebView, a refused permission, plain HTTP) it says so and
 * hands over to the ordinary picker.
 */

import { createLayer } from './layers.js';

export interface CameraTexts {
  title: string;
  capture: string;
  retake: string;
  use: string;
  flip: string;
  close: string;
  starting: string;
  denied: string;
  unavailable: string;
  gallery: string;
}

export const CAMERA_TEXTS: CameraTexts = {
  title: 'Slikaj',
  capture: 'Okini',
  retake: 'Ponovo',
  use: 'Koristi ovu sliku',
  flip: 'Promeni kameru',
  close: 'Zatvori',
  starting: 'Uključujem kameru…',
  denied: 'Kamera nije dozvoljena. Dozvoli pristup kameri u podešavanjima pregledača, ili izaberi sliku iz galerije.',
  unavailable: 'Kamera nije dostupna na ovom uređaju. Izaberi sliku iz galerije.',
  gallery: 'Izaberi iz galerije',
};

/** May this page open the camera itself? (A secure page with camera access.) */
export function cameraAvailable(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (window.isSecureContext === false) return false;
  return typeof navigator.mediaDevices?.getUserMedia === 'function';
}

/**
 * Opens the in-page camera over the page and resolves with the photo taken,
 * or undefined when the shopper closed it. `onGallery` is offered when the
 * camera cannot start; the caller opens the ordinary picker there.
 */
export function takePhoto(options: {
  /** Where the dialog is mounted (the widget's shadow root, for its styles). */
  host: HTMLElement | ShadowRoot;
  facing?: 'user' | 'environment';
  texts?: Partial<CameraTexts>;
  onGallery?: () => void;
}): Promise<File | undefined> {
  const t = { ...CAMERA_TEXTS, ...options.texts };
  return new Promise((resolve) => {
    let facing: 'user' | 'environment' = options.facing ?? 'user';
    let stream: MediaStream | undefined;
    let shot: { blob: Blob; url: string } | undefined;
    let settled = false;

    const dialog = document.createElement('div');
    dialog.className = 'pz-camera';
    dialog.innerHTML = `
      <div class="pz-camera-stage">
        <video class="pz-camera-video" playsinline muted autoplay></video>
        <img class="pz-camera-still" alt="" hidden>
        <p class="pz-camera-note" role="status"></p>
      </div>
      <div class="pz-camera-bar">
        <button type="button" class="pz-camera-close" aria-label="${t.close}">✕</button>
        <button type="button" class="pz-camera-shutter" aria-label="${t.capture}"><span></span></button>
        <button type="button" class="pz-camera-flip" aria-label="${t.flip}" hidden>⟲</button>
        <button type="button" class="pz-camera-retake pz-btn-ghost" hidden>${t.retake}</button>
        <button type="button" class="pz-camera-use pz-btn" hidden>${t.use}</button>
        <button type="button" class="pz-camera-gallery pz-btn" hidden>${t.gallery}</button>
      </div>`;
    const $ = <T extends HTMLElement>(selector: string): T => dialog.querySelector(selector) as T;
    const video = $<HTMLVideoElement>('.pz-camera-video');
    const still = $<HTMLImageElement>('.pz-camera-still');
    const note = $<HTMLParagraphElement>('.pz-camera-note');
    const shutter = $<HTMLButtonElement>('.pz-camera-shutter');
    const flip = $<HTMLButtonElement>('.pz-camera-flip');
    const retake = $<HTMLButtonElement>('.pz-camera-retake');
    const use = $<HTMLButtonElement>('.pz-camera-use');
    const gallery = $<HTMLButtonElement>('.pz-camera-gallery');
    const close = $<HTMLButtonElement>('.pz-camera-close');

    const stop = (): void => {
      for (const track of stream?.getTracks() ?? []) track.stop();
      stream = undefined;
      video.srcObject = null;
    };
    // A layer of its own in the browser's top layer, like the wizard and the
    // lightbox: a plain fixed element opened from inside the wizard (a modal
    // <dialog>) sat BEHIND it — "Slikaj" looked dead on every phone, and the
    // camera stayed on invisibly. The layer also brings Escape, focus return
    // and the toasts along.
    const layer = createLayer({
      host: options.host,
      className: 'pz-camera-layer',
      content: dialog,
      label: t.title,
      closeOnBackdrop: false,
      onClose: () => finish(undefined),
    });
    const finish = (file: File | undefined): void => {
      if (settled) return;
      settled = true;
      stop();
      if (shot) URL.revokeObjectURL(shot.url);
      if (layer.isOpen) layer.close();
      layer.element.remove();
      resolve(file);
    };

    const showing = (mode: 'live' | 'still' | 'error'): void => {
      video.hidden = mode !== 'live';
      still.hidden = mode !== 'still';
      shutter.hidden = mode !== 'live';
      retake.hidden = use.hidden = mode !== 'still';
      gallery.hidden = mode !== 'error' || !options.onGallery;
      if (mode !== 'live') flip.hidden = true;
    };

    const start = async (): Promise<void> => {
      stop();
      showing('live');
      note.textContent = t.starting;
      video.classList.toggle('is-mirrored', facing === 'user');
      try {
        const wanted: MediaStreamConstraints = {
          audio: false,
          video: { facingMode: { ideal: facing }, width: { ideal: 2560 }, height: { ideal: 2560 } },
        };
        try {
          stream = await navigator.mediaDevices.getUserMedia(wanted);
        } catch (error) {
          // A phone that cannot meet the size asks for any camera instead.
          if ((error as { name?: string }).name === 'OverconstrainedError') {
            stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
          } else {
            throw error;
          }
        }
        if (settled) return stop();
        video.srcObject = stream;
        await video.play().catch(() => undefined);
        note.textContent = '';
        // A second camera to switch to? Only known once permission is given.
        const devices = await navigator.mediaDevices.enumerateDevices?.().catch(() => []) ?? [];
        flip.hidden = devices.filter((d) => d.kind === 'videoinput').length < 2;
      } catch (error) {
        const name = (error as { name?: string }).name;
        note.textContent = name === 'NotAllowedError' || name === 'SecurityError' ? t.denied : t.unavailable;
        showing('error');
      }
    };

    shutter.addEventListener('click', () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) return;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      // The preview is mirrored like a mirror; the photo is not — the way the
      // phone's own camera saves a selfie, so text on a shirt reads right.
      canvas.getContext('2d')?.drawImage(video, 0, 0, w, h);
      canvas.toBlob((blob) => {
        canvas.width = canvas.height = 0;
        if (!blob) return;
        if (shot) URL.revokeObjectURL(shot.url);
        shot = { blob, url: URL.createObjectURL(blob) };
        still.src = shot.url;
        stop();
        showing('still');
      }, 'image/jpeg', 0.92);
    });
    retake.addEventListener('click', () => void start());
    use.addEventListener('click', () => {
      if (!shot) return;
      const name = `selfi-${new Date().toISOString().replace(/[:.]/g, '-')}.jpg`;
      finish(new File([shot.blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
    });
    flip.addEventListener('click', () => {
      facing = facing === 'user' ? 'environment' : 'user';
      void start();
    });
    gallery.addEventListener('click', () => {
      finish(undefined);
      options.onGallery?.();
    });
    close.addEventListener('click', () => finish(undefined));

    layer.open();
    close.focus?.();
    void start();
  });
}
